import type { UnifiedSearchResults } from './api/routes/search';

/**
 * RUT-4598: the global search bar fires several searches at once. Read them
 * with Promise.allSettled and keep whatever succeeded, so one failed call
 * (e.g. the templates/areas quick search answering 422) can't blank the bar.
 */

type Settled<T> = PromiseSettledResult<T>;

export interface SearchPartsInput<A> {
  customersRes: Settled<{ hits: any[] }>;
  subscriptionsRes: Settled<{ hits: any[] }>;
  backendRes: Settled<UnifiedSearchResults>;
  addressesRes: Settled<A[]>;
}

export interface SearchParts<A> {
  customerHits: any[];
  subscriptionHits: any[];
  templates: any[];
  serviceAreas: any[];
  addressResults: A[];
}

/** Empty unified-search payload, used when that call fails. */
export function emptyUnifiedResults(query: string): UnifiedSearchResults {
  return {
    results: { customers: [], services: [], templates: [], serviceAreas: [] },
    count: { customers: 0, services: 0, templates: 0, serviceAreas: 0 },
    query,
  };
}

/** Successful parts, failed parts empty. `null` only when every call failed. */
export function settledSearchParts<A>(
  input: SearchPartsInput<A>,
  onError: (label: string, reason: unknown) => void = () => {},
): SearchParts<A> | null {
  const { customersRes, subscriptionsRes, backendRes, addressesRes } = input;
  const all: Array<[string, Settled<unknown>]> = [
    ['customers', customersRes],
    ['subscriptions', subscriptionsRes],
    ['templates/serviceAreas', backendRes],
    ['addresses', addressesRes],
  ];
  for (const [label, res] of all) {
    if (res.status === 'rejected') onError(label, res.reason);
  }
  if (all.every(([, res]) => res.status === 'rejected')) return null;

  const backend = backendRes.status === 'fulfilled' ? backendRes.value : null;
  return {
    customerHits: customersRes.status === 'fulfilled' ? customersRes.value?.hits ?? [] : [],
    subscriptionHits: subscriptionsRes.status === 'fulfilled' ? subscriptionsRes.value?.hits ?? [] : [],
    templates: backend?.results?.templates ?? [],
    serviceAreas: backend?.results?.serviceAreas ?? [],
    addressResults: addressesRes.status === 'fulfilled' ? addressesRes.value ?? [] : [],
  };
}
